import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { MagnifyingGlass, ArrowRight, Globe } from "@phosphor-icons/react";
import DestinationsGrid from "@/components/DestinationsGrid";
import Newsletter from "@/components/Newsletter";
import { DESTINATIONS, REGIONS, waLink } from "@/lib/site";

const REGION_HIGHLIGHTS = {
    Europe: { label: "Europe", desc: "Cobblestone cities, alpine escapes and sun-drenched coastlines." },
    Asia: { label: "Asia", desc: "Ancient temples, neon streets, bullet trains and island getaways." },
    Islands: { label: "Islands", desc: "Overwater villas, coral reefs and barefoot luxury." },
    Adventure: { label: "Adventure", desc: "Safari sunrises, mountain treks, fjords and wild encounters." },
    "Middle East": { label: "Middle East", desc: "Skyline opulence, desert falconry and golden-hour dunes." },
    Americas: { label: "Americas", desc: "Iconic landmarks, canyon roads and the world's best skylines." },
};

export default function Destinations() {
    const [query, setQuery] = useState("");
    const [region, setRegion] = useState("All");

    const filtered = useMemo(() => {
        return DESTINATIONS.filter((d) => {
            const matchQ = [d.name, d.country, d.tag, d.description]
                .join(" ")
                .toLowerCase()
                .includes(query.toLowerCase());
            const matchR = region === "All" || d.region === region;
            return matchQ && matchR;
        });
    }, [query, region]);

    return (
        <div data-testid="page-destinations" className="pt-28">
            {/* Hero */}
            <section className="py-20 bg-navy">
                <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
                    <p className="overline">The Atlas</p>
                    <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-medium leading-[1.05] mt-6 tracking-tight max-w-3xl">
                        Destinations, <em className="italic text-gold">curated for you.</em>
                    </h1>
                    <p className="font-sans text-base text-white/65 mt-6 max-w-2xl leading-relaxed">
                        Search a place, filter by region, or simply wander. Each destination is one we have personally
                        walked, slept in and loved.
                    </p>

                    {/* Search + filter */}
                    <div className="mt-12 grid lg:grid-cols-12 gap-6 items-end border-b border-white/10 pb-6" data-testid="destinations-filter-bar">
                        <div className="lg:col-span-6 relative">
                            <label className="overline mb-3 block">Search</label>
                            <div className="flex items-center gap-3 border-b border-white/20 focus-within:border-gold transition-colors">
                                <MagnifyingGlass size={18} weight="light" className="text-white/50" />
                                <input
                                    type="text"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder="Try 'Paris', 'island', 'safari'..."
                                    data-testid="destinations-search-input"
                                    className="flex-1 bg-transparent outline-none text-white placeholder-white/35 py-3 font-sans"
                                />
                            </div>
                        </div>
                        <div className="lg:col-span-6">
                            <label className="overline mb-3 block">Region</label>
                            <div className="flex flex-wrap gap-2" data-testid="destinations-region-filter">
                                {REGIONS.map((r) => (
                                    <button
                                        key={r}
                                        onClick={() => setRegion(r)}
                                        data-testid={`region-chip-${r.toLowerCase()}`}
                                        className={`px-4 py-2 text-xs uppercase tracking-widest border transition-colors ${
                                            region === r
                                                ? "bg-gold text-navy border-gold"
                                                : "border-white/20 text-white/70 hover:border-gold hover:text-gold"
                                        }`}
                                    >
                                        {r}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Region highlight strip (only when a region is selected) */}
            {region !== "All" && REGION_HIGHLIGHTS[region] && (
                <motion.section
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                    className="bg-[#080E20] border-b border-white/5 py-8"
                >
                    <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24 flex items-center gap-6">
                        <Globe size={28} weight="light" className="text-gold shrink-0" />
                        <div>
                            <p className="font-serif text-xl">{REGION_HIGHLIGHTS[region].label}</p>
                            <p className="font-sans text-sm text-white/60 mt-0.5">{REGION_HIGHLIGHTS[region].desc}</p>
                        </div>
                        <a
                            href={waLink(`Hi! I'm interested in traveling to ${region}. Can you help?`)}
                            target="_blank"
                            rel="noreferrer"
                            className="ml-auto inline-flex items-center gap-2 text-gold text-xs uppercase tracking-widest hover:text-[#E5C158] whitespace-nowrap"
                        >
                            Plan a {region} trip <ArrowRight size={12} weight="bold" />
                        </a>
                    </div>
                </motion.section>
            )}

            <section className="pb-24 lg:pb-32 bg-navy">
                <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
                    <div className="flex items-baseline justify-between mb-8 pt-6">
                        <p className="font-sans text-sm text-white/50">
                            Showing <span className="text-white">{filtered.length}</span> of {DESTINATIONS.length} destinations
                        </p>
                        {query && (
                            <button
                                onClick={() => setQuery("")}
                                className="font-sans text-xs text-white/40 hover:text-gold transition-colors uppercase tracking-widest"
                            >
                                Clear search
                            </button>
                        )}
                    </div>

                    {filtered.length > 0 ? (
                        <DestinationsGrid destinations={filtered} layout="grid" />
                    ) : (
                        <div className="border border-white/10 p-16 text-center" data-testid="destinations-empty">
                            <p className="font-serif text-2xl text-white">No destinations match that search.</p>
                            <p className="font-sans text-sm text-white/55 mt-2">Try a different region or keyword.</p>
                            <button
                                onClick={() => { setQuery(""); setRegion("All"); }}
                                className="mt-6 inline-flex items-center gap-2 text-gold text-xs uppercase tracking-widest hover:text-[#E5C158]"
                            >
                                Show all destinations <ArrowRight size={12} weight="bold" />
                            </button>
                        </div>
                    )}
                </div>
            </section>

            {/* Bespoke CTA */}
            <section className="py-20 bg-[#080E20] border-t border-white/5" data-testid="destinations-bespoke">
                <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
                    <div className="grid lg:grid-cols-12 gap-10 items-center border border-white/10 p-10 lg:p-14">
                        <div className="lg:col-span-8">
                            <p className="overline">Don't see your dream destination?</p>
                            <h2 className="font-serif text-3xl sm:text-4xl font-medium mt-4 leading-tight">
                                We go <em className="italic text-gold">anywhere.</em> Just ask.
                            </h2>
                            <p className="font-sans text-sm text-white/60 mt-4 leading-relaxed max-w-xl">
                                Our atlas is curated, not exhaustive. If you have a destination in mind — however remote or unusual — we'll research it, vet it, and build you something extraordinary.
                            </p>
                        </div>
                        <div className="lg:col-span-4 flex flex-col gap-3">
                            <a
                                href={waLink("Hi! I have a custom destination in mind and would love your help planning it.")}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center justify-center gap-2 bg-gold text-navy hover:bg-[#E5C158] px-7 py-4 font-sans font-medium uppercase tracking-wider text-xs transition-colors"
                            >
                                Request a custom trip <ArrowRight size={14} weight="bold" />
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            <Newsletter />
        </div>
    );
}
